import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Country } from "country-state-city";
import { toast } from "sonner";
import useRequest from "../../../components/hook/use-req";
// The test-number routes live on journeybuilder, not api.connect — that is
// the service that owns Preview Bot and the journey definitions this number
// is tested against. /auth/get_userdata below stays on useRequest because
// the user profile genuinely is an api.connect resource.
import useJouneyReq from "../../../components/hook/bot-req";

// Same dial-code list + default-to-admin's-own-country pattern used by the
// Team "Invite" modal (crm-pages/team/index.tsx) and lead-qr-section.tsx —
// kept in sync with that approach rather than the narrower NG/IN-only
// ALLOWED_COUNTRIES list the signup flow uses, since a test number isn't
// restricted to the 2 countries signup validates against.
const normalizeDialCode = (phoneCode?: string | number | null) => {
  if (!phoneCode) return "+234";
  const code = String(phoneCode);
  return code.startsWith("+") ? code : `+${code}`;
};

const countryCodeOptions = Country.getAllCountries()
  .map((c) => ({
    value: normalizeDialCode(c.phonecode),
    label: `${normalizeDialCode(c.phonecode)} ${c.isoCode}`,
    isoCode: c.isoCode,
  }))
  .sort((a, b) => a.value.localeCompare(b.value));

const getCountryByDialCode = (dialCode?: string | number | null) =>
  countryCodeOptions.find((c) => c.value === normalizeDialCode(dialCode));

// Flag + dial-code button with a searchless dropdown list.
const CountryCodeSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = getCountryByDialCode(value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((s) => !s)}
        className="flex h-[45px] items-center gap-1.5 rounded-l-lg bg-gray-50 dark:bg-gray-800 border-r border-gray-300 dark:border-gray-600 px-3 text-sm text-gray-700 dark:text-gray-200 disabled:cursor-not-allowed"
      >
        {selected && (
          <img
            src={`https://flagcdn.com/24x18/${selected.isoCode.toLowerCase()}.png`}
            alt=""
            className="h-3.5 w-5 rounded-sm object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        )}
        <span>{value}</span>
        <ChevronDown size={14} className={`text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          {countryCodeOptions.map((option) => (
            <button
              key={option.isoCode}
              type="button"
              onClick={() => { onChange(option.value); setOpen(false); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                option.value === value ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400" : "text-gray-700 dark:text-gray-200"
              }`}
            >
              <img
                src={`https://flagcdn.com/24x18/${option.isoCode.toLowerCase()}.png`}
                alt=""
                className="h-3.5 w-5 rounded-sm object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Testing & Preview settings tab ───────────────────────────────────────────
//
// Lets an org configure one WhatsApp number to safely trigger bots/QR
// lead-flows/broadcast previews against without touching real customers.
//
// OTP verification was removed (2026-08-12, per direct request) — saving now
// goes straight to PUT /settings/test-number with the phone number included
// alongside the existing toggle fields, instead of the old
// send-otp -> verify-otp round trip. GET/PUT `/settings/test-number` are
// served by journey_builder's routes/test_number.py (same as before); the
// two OTP-only routes are no longer called from here at all.

type Step = "editing" | "saved";

interface ToggleState {
  botTesting: boolean;
  qrLeadForm: boolean;
  broadcastPreviews: boolean;
}

interface TestNumberSettingsResponse {
  phone_number?: string;
  bot_testing?: boolean;
  qr_lead_form?: boolean;
  broadcast_previews?: boolean;
}

const TestingPreviewSettings: React.FC = () => {
  const token = localStorage.getItem("authToken");

  // Defaults to the admin's own profile country (same source/pattern as
  // Team > Invite and lead-qr-section.tsx) until that fetch resolves, then
  // the dropdown is free to pick any other country from here.
  const [countryCode, setCountryCode] = useState("+234");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [step, setStep] = useState<Step>("editing");
  const [savedNumber, setSavedNumber] = useState<string | null>(null);

  const [toggles, setToggles] = useState<ToggleState>({
    botTesting: true,
    qrLeadForm: true,
    broadcastPreviews: true,
  });
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [savingNumber, setSavingNumber] = useState(false);
  const [savingToggle, setSavingToggle] = useState<keyof ToggleState | null>(null);

  const { makeRequest: fetchTestNumber } = useJouneyReq("/settings/test-number", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: getCurrentUser } = useRequest("/auth/get_userdata", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: updateTestNumber } = useJouneyReq("/settings/test-number", "PUT", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const load = async () => {
      setLoadingInitial(true);
      const [response, status] = await fetchTestNumber();
      if (status >= 200 && status < 300 && response) {
        const data: TestNumberSettingsResponse = response.payload || response;
        if (data.phone_number) {
          setSavedNumber(data.phone_number);
          setStep("saved");
        }
        setToggles({
          botTesting: data.bot_testing ?? true,
          qrLeadForm: data.qr_lead_form ?? true,
          broadcastPreviews: data.broadcast_previews ?? true,
        });
      }
      setLoadingInitial(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default the test number's country code to the logged-in admin's own
  // profile country — same source/pattern Team > Invite and
  // lead-qr-section.tsx use — rather than hardcoding Nigeria for everyone.
  // The dropdown remains free to pick any other country afterward.
  useEffect(() => {
    (async () => {
      const [response] = await getCurrentUser();
      const code = response?.payload?.country_code;
      if (code) setCountryCode(normalizeDialCode(code));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneDigits(e.target.value.replace(/\D/g, ""));
  };

  const resetForm = () => {
    setPhoneDigits("");
    setStep("editing");
  };

  const handleSaveNumber = async () => {
    if (phoneDigits.length < 7) {
      toast.error("Enter a valid phone number");
      return;
    }
    setSavingNumber(true);
    const fullNumber = `${countryCode}${phoneDigits}`;
    const [response, status] = await updateTestNumber({
      phone_number: fullNumber,
      bot_testing: toggles.botTesting,
      qr_lead_form: toggles.qrLeadForm,
      broadcast_previews: toggles.broadcastPreviews,
    });
    setSavingNumber(false);

    if (status >= 200 && status < 300) {
      toast.success("Test number saved");
      setSavedNumber((response?.payload || response)?.phone_number || fullNumber);
      setStep("saved");
    } else {
      toast.error(response?.detail || response?.message || "Failed to save test number");
    }
  };

  const handleEditNumber = () => {
    // Split the saved "+234-9900990099" back into code/digits so the
    // editable fields aren't blank when the operator wants to change it.
    if (savedNumber) {
      // Split against the real dial-code list, longest first. A regex like
      // /^(\+\d{1,4})(\d+)$/ is greedy and reads "+2349900990099" as code
      // "+2349", so opening Edit would silently change the country.
      const digits = savedNumber.replace(/\D/g, "");
      const dial = countryCodeOptions
        .map((c) => c.value)
        .filter((v) => digits.startsWith(v.slice(1)))
        .sort((a, b) => b.length - a.length)[0];
      if (dial) {
        setCountryCode(dial);
        setPhoneDigits(digits.slice(dial.length - 1));
      } else {
        setPhoneDigits(digits);
      }
    }
    setStep("editing");
  };

  const handleToggle = async (key: keyof ToggleState) => {
    const next = { ...toggles, [key]: !toggles[key] };
    setToggles(next);
    setSavingToggle(key);
    // Same PUT as the number save — the toggles-only hook went away with the
    // OTP flow. Sending no phone_number leaves the stored number untouched.
    const [response, status] = await updateTestNumber({
      bot_testing: next.botTesting,
      qr_lead_form: next.qrLeadForm,
      broadcast_previews: next.broadcastPreviews,
    });
    setSavingToggle(null);

    if (!(status >= 200 && status < 300)) {
      // Roll back — don't leave the switch showing a state that didn't save.
      setToggles(toggles);
      toast.error(response?.detail || response?.message || "Failed to update setting");
    }
  };

  if (loadingInitial) {
    return <div className="py-12 text-center text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading…</div>;
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Testing and Preview</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Configure a test phone number to safely try out features like bots, QR Lead flows, templates, automations and broadcast previews before they go live
      </p>

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* ── WhatsApp Test Number card ── */}
        <div className="w-full lg:max-w-[420px] border border-gray-200 dark:border-gray-700 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">WhatsApp Test Number</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {step === "saved"
              ? "Edit the WhatsApp number you want to use for testing across ConvoHub"
              : "Enter the WhatsApp number you want to use for testing across ConvoHub"}
          </p>

          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">WhatsApp Number</p>

          {step === "saved" && savedNumber ? (
            <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg h-[45px] px-3 bg-emerald-50 dark:bg-emerald-900/10">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{savedNumber}</span>
            </div>
          ) : (
            <div className="flex items-center border rounded-lg h-[45px] focus-within:ring-2 focus-within:ring-[#4F46E5] focus-within:border-transparent border-gray-300 dark:border-gray-600">
              <CountryCodeSelect
                value={countryCode}
                onChange={(value) => {
                  setCountryCode(value);
                  setPhoneDigits("");
                }}
              />
              <input
                type="tel"
                value={phoneDigits}
                onChange={handlePhoneChange}
                placeholder="801 234 5678"
                className="flex-1 px-2 py-2 bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none text-sm"
              />
            </div>
          )}

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Use this number to safely test ConvoHub features before going live
          </p>

          <div className="flex items-center gap-4 mt-4">
            {step === "saved" ? (
              <button
                type="button"
                onClick={handleEditNumber}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
              >
                Edit Number
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSaveNumber}
                  disabled={savingNumber}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingNumber ? "Saving…" : "Save Number"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                >
                  Reset
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Use this number for card ── */}
        <div className="w-full lg:max-w-[420px] border border-gray-200 dark:border-gray-700 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Use this number for</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Choose where this test number will be used</p>
          {step === "saved" && savedNumber && (
            <span className="inline-block text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded px-2 py-1 mb-3">
              {savedNumber}
            </span>
          )}

          <ToggleRow
            title="Bot Testing"
            subtitle="Test Bot Flows and Behaviours"
            checked={toggles.botTesting}
            saving={savingToggle === "botTesting"}
            onChange={() => handleToggle("botTesting")}
          />
          <ToggleRow
            title="QR Code Lead Form"
            subtitle="Capture and test leads via QR Code"
            checked={toggles.qrLeadForm}
            saving={savingToggle === "qrLeadForm"}
            onChange={() => handleToggle("qrLeadForm")}
          />
          <ToggleRow
            title="Broadcast Previews"
            subtitle="Preview broadcast messages before sending"
            checked={toggles.broadcastPreviews}
            saving={savingToggle === "broadcastPreviews"}
            onChange={() => handleToggle("broadcastPreviews")}
          />
        </div>
      </div>
    </div>
  );
};

const ToggleRow: React.FC<{
  title: string;
  subtitle: string;
  checked: boolean;
  saving: boolean;
  onChange: () => void;
}> = ({ title, subtitle, checked, saving, onChange }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
    <div>
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
    </div>
    <button
      type="button"
      onClick={onChange}
      disabled={saving}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none shrink-0 disabled:opacity-60 ${
        checked ? "bg-green-500" : "bg-gray-200 dark:bg-gray-700"
      }`}
      aria-label={`Toggle ${title}`}
    >
      <span
        className={`inline-block h-4.5 w-4.5 h-[18px] w-[18px] transform rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? "translate-x-[22px]" : "translate-x-1"
        }`}
      />
    </button>
  </div>
);

export default TestingPreviewSettings;